import React from 'react';

import bgLogin from '@assets/img/arts/bg-login.png';
import d2d from '@assets/img/icons/d2d-new.svg';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { TextInput } from '@components/Forms/TextInput';
import { Layout } from '@components/Layout';
import { SpinnerModal } from '@components/SpinnerModal';
import { useForgetPassword } from '@hooks/mutation/useForgetPassword';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { FormError } from '@utils/errors';
import { formStrings, createValidate, newEmailField } from '@utils/forms';
import { createStrings } from '@utils/local';
import { PageMeta } from '@utils/pages';
import { Form, Formik } from 'formik';
import * as t from 'io-ts';

const strings = createStrings({
  ja: {
    title: 'パスワード再設定',
    prompt: 'D2Dに登録されていたメールアドレスを入力してください。',
    email: 'メールアドレス',
    explanation: () => (
      <>
        ご登録されているメールアドレスに認証リンクを送信します。
        <br />
        メールアドレスをお忘れの場合は、お手数ですが
        <a rel="noreferrer" target="_blank" href="mailto:cs@fcstandard.com">
          ヘルプデスク
        </a>
        にお問い合わせください。
      </>
    ),
    resetNotification: 'パスワード再設定用のメールを送りました。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.backgroundCenter,
    flex: 1,
    backgroundImage: `url(${bgLogin})`,
    '& a:hover': {
      color: theme.palette.primary.light,
    },
    '& a': {
      textDecoration: 'underline',
      color: theme.palette.primary.main,
    },
  },
  title: {
    ...common.flexInline,
    ...common.biggerText,
    alignItems: 'center',
    fontSize: 30,
    lineHeight: '30px',
    color: theme.palette.text.primary,
    justifyContent: 'center',
    marginBottom: 50,
    [theme.breakpoints.down('sm')]: {
      ...common.flexColumnCenter,
    },
  },
  inner: {
    maxWidth: 680,
    margin: '0 auto',
    padding: '0 1rem',
  },
  alertContainer: {
    ...common.flexColumn,
    justifyContent: 'center',
    height: 130,
  },
  formContainer: {
    ...common.flexColumn,
    ...common.verticalSpace2,
    padding: '90px 100px',
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down('sm')]: {
      padding: '20px',
    },
  },
  logo: {
    display: 'block',
    width: 130,
    height: 30,
    alignSelf: 'center',
  },
  helpContainer: {
    ...common.flexInline,
    justifyContent: 'space-between',
  },
  helpLink: {
    ...common.baseButton,
    ...common.middleText,
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  prompt: {
    ...common.flexInline,
    ...common.middleText,
    justifyContent: 'center',
    color: theme.palette.grey['500'],
  },
  explanation: {
    ...common.middleText,
    color: theme.palette.grey['500'],
    whiteSpace: 'pre-wrap',
    marginBottom: '2rem',
  },
}));

const schema = t.type({
  email: newEmailField(strings.email),
});

type Values = t.TypeOf<typeof schema>;

const initialValues: Values = {
  email: '',
};

export const ForgetPassword: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { mutateAsync, isSuccess, isLoading, isError, error, reset } = useForgetPassword();

  return (
    <Layout title={strings.title}>
      <SpinnerModal open={isLoading} />
      <div className={classes.container}>
        <div className={classes.inner}>
          <Formik
            validate={createValidate(schema)}
            initialValues={initialValues}
            onSubmit={async (values) => {
              await mutateAsync(values);
            }}
          >
            <>
              <div className={classes.alertContainer}>
                <Alert open={isError} severity="error" onClose={reset}>
                  {error instanceof FormError && error.getDisplayMessage()}
                </Alert>

                <Alert open={isSuccess} severity="success" onClose={reset}>
                  {strings.resetNotification}
                </Alert>
              </div>
              <Form className={classes.formContainer}>
                <div className={classes.title}>
                  <img src={d2d} className={classes.logo} alt="D2D Logo" />
                  <div style={{ width: '1rem' }} />
                  {strings.title}
                </div>

                <div className={classes.prompt}>{strings.prompt}</div>

                <TextInput
                  name="email"
                  type="email"
                  autoComplete="email"
                  noLabel
                  itemVariant="column"
                  iconBefore={
                    <PersonOutlineIcon
                      style={{ paddingRight: 10, width: '1.6rem', height: '1.6rem' }}
                    />
                  }
                />

                <div className={classes.explanation}>{strings.explanation()}</div>

                <Button text={formStrings.confirm} color="primary" type="submit" />
              </Form>
            </>
          </Formik>
        </div>
      </div>
    </Layout>
  );
};

export const meta: PageMeta = {
  title: strings.title,
};
